import { useEffect, useMemo } from "react";
import { useWatch } from "react-hook-form";
import { Disclosure } from "@headlessui/react";
import {
  useFormContext,
  useFieldArray,
} from "react-hook-form";
import CountryMultiSelect from "../common/CountryMultiSelect";
import PostalCodeSelect   from "../common/PostalCodeSelect";
import MethodCard         from "./MethodCard";
import { MAX_METHODS }    from "../constants/validation";
import countryList from "react-select-country-list";
import Select from "react-select";
const compactStyles = {
  control: (base) => ({ ...base, minHeight: 32, height: 32 }),
  valueContainer: (base) => ({ ...base, paddingTop: 0, paddingBottom: 0 }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base) => ({ ...base, height: 32 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

export default function ZoneCard({ index, onRemove }) {
  const {
    register,
    control,
    watch,
    trigger,
    setValue,
    formState: { errors },
  } = useFormContext();

  const path = `shippingGroups.${index}`;
  const allCountries = useMemo(() => countryList().getData(), []);
  const labelByIso = useMemo(
    () => Object.fromEntries(allCountries.map(o => [o.value, o.label])),
    [allCountries]
  );
  const watchedCountries   = useWatch({ control, name: `${path}.countries` });
  const watchedPostalRules = useWatch({ control, name: `${path}.postalRules` });
  useEffect(() => {
    // whenever countries OR postal rules change, re-run full zones validation
    trigger("shippingGroups");
  }, [watchedCountries, watchedPostalRules, trigger]);
  const zoneErrors = errors?.shippingGroups?.[index] || {};
  const countriesWatch  = useWatch({ control, name: `${path}.countries` });
  const postalRulesWatch = useWatch({ control, name: `${path}.postalRules` });
  useEffect(() => {
    const cs    = watchedCountries     || [];
    const rules = watchedPostalRules  || {};
    const filtered = Object.fromEntries(Object.entries(rules).filter(([iso]) => cs.includes(iso)));
    if (JSON.stringify(rules) !== JSON.stringify(filtered)) {
      setValue(`${path}.postalRules`, filtered, { shouldDirty: true, shouldValidate: true });
      trigger("shippingGroups");
    }
  }, [watchedCountries, watchedPostalRules, setValue, trigger]);
  /* ----- methods array hook ----------------------------------------- */
  const {
    fields: methodFields,
    append: appendMethod,
    remove: removeMethod,
  } = useFieldArray({
    control,
    name: `${path}.methods`,
  });

  /* ----- postal code array hook ------------------------------------- */
  const pcArray = useFieldArray({
    control,
    name: `${path}.postalCodes`,
  });
  const currentRows   = watch(`${path}.postalCodes`) || [];
  const currentMode   = currentRows.length ? !!currentRows[0]?.include : true; // default: include
  const addPostal = () => {
    pcArray.append({ code: "", include: currentMode }); // keep mode consistent
    // revalidate zone + cross-zone overlaps
    trigger("shippingGroups");
  };

  // If user flips include on one row, propagate to all rows in that zone
  const onChangeIncludeAll = (val) => {
    const rows = (watch(`${path}.postalCodes`) || []).map(r => ({ ...r, include: val }));
    setValue(`${path}.postalCodes`, rows, { shouldDirty: true, shouldValidate: true });
    trigger("shippingGroups");
  };
  /* ------- helpers --------------------------------------------------- */
  const addMethod = () => {
    if (methodFields.length >= MAX_METHODS) return;
    appendMethod({
      name: "",
      type: "flat",
      basis: "weight",
      enabled: true,
      estimatedTime: "",
      etaMin: undefined,
      etaMax: undefined,
      etaUnit: "business_days",
      useCourierApi: false,
      courier: "UPS",
      rules: { rate: undefined },
      freeShipping: { enabled: false, conditions: {}, message: "" },
    });
    trigger(`shippingGroups.${index}.methods`);
  };
  const allIso = useMemo(
    () => countryList().getData().map((c) => c.value),
    []
  );
  // All other zones (to compute already-used countries)
  const allGroups = useWatch({ control, name: "shippingGroups" }) || [];

  // Countries fully covered by other zones (no postal-code partition there)
  const disabledCountries = useMemo(() => {
    const disabled = new Set();

    allGroups.forEach((g, i) => {
      if (i === index) return;
      const countries = g?.countries || [];
      countries.forEach((iso) => {
        const rule = g?.postalRules?.[iso];
        const hasCodes =
          !!rule &&
          Array.isArray(rule.codes) &&
          rule.codes.filter(c => (c?.code || "").trim()).length > 0;

        // Only disable if whole-country coverage (no rule or empty codes array)
        if (!hasCodes) disabled.add(iso);
      });
    });

    return Array.from(disabled);
  }, [allGroups, index]);

  const selectAllUnused = () => {
    const available = allIso.filter((iso) => !disabledCountries.includes(iso));
    setValue(`${path}.countries`, available, { shouldDirty: true, shouldValidate: true });
    trigger("shippingGroups");
  };
    const ensurePostalRule = (iso, mode = "include") => {
    const rules = watch(`${path}.postalRules`) || {};
    if (rules[iso]) return;
    const next = { ...rules, [iso]: { mode, codes: [] } };
    setValue(`${path}.postalRules`, next, { shouldDirty: true, shouldValidate: true });
    trigger("shippingGroups");
  };

  const removePostalRule = (iso) => {
    const rules = watch(`${path}.postalRules`) || {};
    if (!rules[iso]) return;
    const { [iso]: _, ...rest } = rules;
    setValue(`${path}.postalRules`, rest, { shouldDirty: true, shouldValidate: true });
    trigger("shippingGroups");
  };

  /* UI ---------------------------------------------------------------- */
  return (
    <Disclosure defaultOpen>
      {({ open }) => (
        <div className="rounded-lg border p-4 shadow-sm">
          {/* Header / Zone name --------------------------------------- */}
          <Disclosure.Button className="flex w-full items-center justify-between">
            <input
              {...register(`${path}.name`)}
              maxLength={60}
              placeholder="Zone name (e.g., Europe)"
              className={`flex-1 rounded border p-2 text-sm ${
                zoneErrors?.name ? "border-red-500" : "border-gray-300"
              }`}
            />
            <span className="ml-2 text-gray-500">{open ? "▴" : "▾"}</span>
          </Disclosure.Button>
          {zoneErrors?.name && (
            <p className="text-xs text-red-600 mt-1">
              {zoneErrors.name.message}
            </p>
          )}

          {/* Body ------------------------------------------------------ */}
          <Disclosure.Panel className="mt-4 space-y-4">
            {/* Countries ------------------------------------------- */}
            <div>
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium">Countries *</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={selectAllUnused}
                    className="text-xs text-indigo-600 hover:underline"
                    title="Pick all countries not used by other zones"
                  >
                    Select all unused
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setValue(`${path}.countries`, [], { shouldDirty: true, shouldValidate: true });
                      trigger("shippingGroups");
                    }}
                    className="text-xs text-gray-600 hover:underline"
                  >
                    Clear
                  </button>
                </div>
              </div>
              <CountryMultiSelect
                name={`${path}.countries`}
                control={control}
                disabledValues={disabledCountries}
              />
              {zoneErrors?.countries && (
                <p className="text-xs text-red-600 mt-1">
                  {zoneErrors.countries.message}
                </p>
              )}
            </div>
            {/* Postal rule adder (only shows countries without a rule yet) */}
            <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_auto] items-start">
              <Select
                classNamePrefix="rs"
                styles={compactStyles}
                placeholder="Add postal rule for…"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                options={(watch(`${path}.countries`) || [])
                  .filter((iso) => !watch(`${path}.postalRules`)?.[iso])
                  .map((iso) => ({ value: iso, label: labelByIso[iso] || iso }))}
                onChange={(opt) => opt && ensurePostalRule(opt.value)}
                isClearable
              />
              {Object.keys(watch(`${path}.postalRules`) || {}).length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setValue(`${path}.postalRules`, {}, { shouldDirty: true, shouldValidate: true });
                    trigger("shippingGroups");
                  }}
                  className="text-xs text-gray-600 hover:underline"
                >
                  Clear all rules
                </button>
              )}
            </div>
            {/* Render editors only for countries that have rules (unchanged below) */}
            {Object.keys(watch(`${path}.postalRules`) || {}).map((iso) =>
              (watch(`${path}.countries`) || []).includes(iso) ? (
                <CountryPostalEditor
                  key={iso}
                  path={path}
                  iso={iso}
                  countryName={labelByIso[iso] || iso}
                  control={control}
                  register={register}
                  watch={watch}
                  setValue={setValue}
                  trigger={trigger}
                  zoneErrors={zoneErrors}
                />
              ) : null
            )}
            {/* Methods list ---------------------------------------- */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium uppercase tracking-wide text-gray-700">
                  Shipping Methods
                </h3>
                <button
                  type="button"
                  onClick={addMethod}
                  disabled={methodFields.length >= MAX_METHODS}
                  className={`rounded px-3 py-1 text-xs text-white shadow ${
                    methodFields.length >= MAX_METHODS
                      ? "bg-gray-400 opacity-60"
                      : "bg-indigo-600 hover:bg-indigo-700"
                  }`}
                >
                  + Add Method
                </button>
              </div>

              {methodFields.length === 0 && (
                <p className="text-xs text-gray-500">No methods yet</p>
              )}
              {/* ⛔ array-level errors (min, duplicates, courier) */}
              {zoneErrors?.methods?.message && (
                <p className="text-xs text-red-600 mb-2">
                  {zoneErrors.methods.message}
                </p>
              )}
              {methodFields.map((m, idx) => (
                <MethodCard
                  key={m.id}
                  zoneIndex={index}
                  methodIndex={idx}
                  onRemove={() => removeMethod(idx)}
                />
              ))}
            </div>

            {/* Remove Zone ----------------------------------------- */}
            <button
              type="button"
              onClick={onRemove}
              className="text-xs text-red-500 hover:underline"
            >
              Remove Zone
            </button>
          </Disclosure.Panel>
        </div>
      )}
    </Disclosure>
  );
}
function CountryPostalEditor({ path, iso,countryName, control, register, watch, setValue, trigger, zoneErrors }) {
  const mode = watch(`${path}.postalRules.${iso}.mode`) || "include";
  const nameCodes = `${path}.postalRules.${iso}.codes`;
  const { fields, append, remove } = useFieldArray({ control, name: nameCodes });
  const codesWatch = useWatch({ control, name: nameCodes });
  useEffect(() => { trigger("shippingGroups"); }, [codesWatch, trigger]);
  const countryErrors = zoneErrors?.postalRules?.[iso];
  const count = (codesWatch || []).filter(c => (c?.code || "").trim()).length;

  return (
    <Disclosure defaultOpen={false}>
      {({ open }) => (
        <div className="mb-2 rounded border">
          <Disclosure.Button className="w-full flex items-center justify-between px-2 py-1 text-xs">
            <div className="flex items-center gap-2">
              <span className="font-medium">{countryName}</span>
              <span className="text-gray-500">({iso}) • {mode} • {count} code{count === 1 ? "" : "s"}</span>
            </div>
            <span className="text-gray-500">{open ? "▴" : "▾"}</span>
          </Disclosure.Button>

          <Disclosure.Panel className="px-2 pb-2">
            <div className="mb-1 flex items-center justify-between">
              <label className="text-xs">Mode</label>
              <select
                value={mode}
                onChange={(e) => {
                  setValue(`${path}.postalRules.${iso}.mode`, e.target.value, { shouldDirty: true, shouldValidate: true });
                  trigger("shippingGroups");
                }}
                className="rounded border p-1 text-xs"
              >
                <option value="include">Include</option>
                <option value="exclude">Exclude</option>
              </select>
            </div>

            {countryErrors?.message && (
              <p className="mb-1 text-xs text-red-600">{countryErrors.message}</p>
            )}

            {fields.map((f, idx) => {
              const codeErr = countryErrors?.codes?.[idx]?.code;
              return (
                <div key={f.id} className="mb-1 flex items-center gap-2 text-xs">
                  <PostalCodeSelect
                    name={`${nameCodes}.${idx}.code`}
                    control={control}
                    country={iso}
                  />
                  <button
                    type="button"
                    onClick={() => { remove(idx); trigger("shippingGroups"); }}
                    className="text-red-500 hover:underline"
                  >
                    ✕
                  </button>
                  {codeErr && <p className="text-xs text-red-600">{codeErr.message}</p>}
                </div>
              );
            })}

            <button
              type="button"
              onClick={() => { append({ code: "" }); trigger("shippingGroups"); }}
              className="rounded bg-indigo-600 px-2 py-0.5 text-[10px] text-white hover:bg-indigo-700"
            >
              + Add code
            </button>
          </Disclosure.Panel>
        </div>
      )}
    </Disclosure>
  );
}
